import React, { useEffect, useRef, useState } from 'react';
import { Loader2, MessageSquare, EyeOff, Pencil } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  fetchReplayQuestions, fetchReplayQuestionSummary, askReplayQuestion, answerReplayQuestion, hideReplayQuestion,
  type ReplayQuestion, type ReplayQuestionList, type ReplayQuestionSummary,
} from '@/services/creatorApi';

// Replay Q&A (Oct 11 2026; thread layout Oct 27). Anyone who has the replay asks
// the creator a question right under the player; the creator answers in the same
// place and the answer shows inline for everyone. It reads like a conversation:
// oldest first, each question a message from the asker, the host's answer a reply
// beneath it, and the box to ask at the bottom like a chat composer. Emails both
// ways are sent server side.
const QUESTION_MIN = 10;
const QUESTION_MAX = 1000;
const ANSWER_MAX = 2000;

const card: React.CSSProperties = {
  padding: 16,
  borderRadius: 'var(--hj-r-md)',
  background: 'var(--hj-surface-1)',
  border: '1px solid var(--hj-hairline)',
};

const pillBtn = (disabled: boolean): React.CSSProperties => ({
  padding: '8px 16px',
  borderRadius: 999,
  background: 'var(--hj-blue)',
  border: '1px solid var(--hj-blue)',
  color: 'oklch(0.18 0.005 70)',
  fontSize: 13,
  fontWeight: 700,
  fontFamily: 'inherit',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
  minHeight: 36,
});

const ghostBtn: React.CSSProperties = {
  background: 'transparent',
  border: 0,
  padding: '6px 4px',
  fontSize: 12.5,
  fontWeight: 600,
  color: 'var(--hj-ink-2)',
  cursor: 'pointer',
  fontFamily: 'inherit',
  minHeight: 32,
};

// A round initial, like a chat app. The host's is yellow so the replies stand out.
const Avatar: React.FC<{ name: string; host?: boolean }> = ({ name, host }) => (
  <span
    aria-hidden
    className="flex-shrink-0 inline-flex items-center justify-center"
    style={{
      width: 32, height: 32, borderRadius: '50%', fontSize: 13, fontWeight: 800,
      background: host ? 'var(--hj-yellow)' : 'var(--hj-surface-2)',
      color: host ? 'oklch(0.18 0.005 70)' : 'var(--hj-ink)',
      border: host ? 0 : '1px solid var(--hj-hairline)',
    }}
  >
    {(name || '?').trim().charAt(0).toUpperCase()}
  </span>
);

// "Today", "Yesterday", or the date: enough to read a thread, no clock needed.
const when = (iso: string) => {
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const Bubble: React.FC<{ text: string; host?: boolean }> = ({ text, host }) => (
  <p
    className="m-0"
    style={{
      fontSize: 14.5, color: 'var(--hj-ink)', lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
      padding: '10px 14px', borderRadius: 14,
      background: host ? 'var(--hj-yellow-tint)' : 'var(--hj-surface-2)',
      border: host ? '1px solid var(--hj-yellow)' : '1px solid var(--hj-hairline)',
    }}
  >
    {text}
  </p>
);

const AnswerBox: React.FC<{
  productId: string;
  q: ReplayQuestion;
  onSaved: (answer: string, answeredAt: string) => void;
  onCancel?: () => void;
}> = ({ productId, q, onSaved, onCancel }) => {
  const { toast } = useToast();
  const [text, setText] = useState(q.answer || '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!text.trim() || saving) return;
    setSaving(true);
    try {
      const r = await answerReplayQuestion(productId, q._id, text.trim());
      onSaved(r.answer, r.answeredAt);
      toast({ title: q.answer ? 'Answer updated' : 'Answer posted', description: q.answer ? undefined : `We emailed ${q.askerFirst}.` });
    } catch (e: any) {
      toast({ title: 'Could not save', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="flex flex-col gap-2" style={{ marginTop: 8 }}>
      <Textarea
        placeholder="Write your answer"
        value={text}
        onChange={e => setText(e.target.value.substring(0, ANSWER_MAX))}
        rows={3}
      />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="mono-label tabular" style={{ color: 'var(--hj-ink-2)', fontFamily: 'var(--hj-font-mono)' }}>
          {text.length}/{ANSWER_MAX}
        </span>
        <div className="flex items-center gap-2">
          {onCancel && <button type="button" style={ghostBtn} onClick={onCancel}>Cancel</button>}
          <button type="button" onClick={save} disabled={!text.trim() || saving} style={pillBtn(!text.trim() || saving)} className="inline-flex items-center gap-1.5">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
            {q.answer ? 'Save answer' : 'Post answer'}
          </button>
        </div>
      </div>
    </div>
  );
};

// "4 questions · 3 answered". Threads, not just a total: the answered count is the
// proof the host actually shows up.
const countLine = (total: number, answered: number) =>
  total === 0 ? '' : `${total} question${total === 1 ? '' : 's'} · ${answered} answered`;

const PromiseBox: React.FC<{ until: string; text: string }> = ({ until, text }) => (
  <div
    className="flex items-start gap-2.5"
    style={{ padding: '12px 14px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)' }}
  >
    <MessageSquare className="w-4 h-4 flex-shrink-0" style={{ marginTop: 2 }} />
    <p className="m-0" style={{ fontSize: 14, lineHeight: 1.45 }}>
      <strong>Active replay until {new Date(until).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}.</strong>{' '}
      {text}
    </p>
  </div>
);

const Heading: React.FC<{ title?: string; sub?: string }> = ({ title = 'Questions', sub }) => (
  <div className="flex items-baseline gap-3 flex-wrap">
    <h2 className="font-hj-display m-0" style={{ fontSize: 22, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700 }}>
      {title}
    </h2>
    {sub && <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--hj-ink)' }}>{sub}</span>}
  </div>
);

// What someone without the replay sees on the class page: the promise, the counts,
// and the host's answers. Askers are anonymous here. Renders nothing when there is
// no promise and nothing answered yet.
const PublicQuestions: React.FC<{ data: ReplayQuestionSummary }> = ({ data }) => {
  const live = data.activeReplay?.live;
  if (!live && data.answers.length === 0) return null;
  const who = data.creatorFirst;
  return (
    <section id="questions" style={{ marginTop: 32, scrollMarginTop: 80 }} className="flex flex-col gap-4">
      <Heading sub={countLine(data.total, data.answered)} />
      {live && data.activeReplay && (
        <PromiseBox until={data.activeReplay.until} text={`Get the replay and ask ${who} anything. Every question is answered within 24 hours.`} />
      )}
      {data.answers.length > 0 && (
        <div className="flex flex-col gap-3">
          {data.answers.map(q => (
            <div key={q._id} className="flex flex-col gap-2.5">
              <div className="flex items-start gap-2.5">
                <Avatar name="A" />
                <div className="flex-1 min-w-0 flex flex-col gap-1">
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--hj-ink)' }}>A student asked</span>
                  <Bubble text={q.question} />
                </div>
              </div>
              <div className="flex items-start gap-2.5" style={{ paddingLeft: 24 }}>
                <Avatar name={who} host />
                <div className="flex-1 min-w-0 flex flex-col gap-1">
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--hj-ink)' }}>{who} answered</span>
                  <Bubble text={q.answer} host />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

/**
 * Questions under the replay. With `publicFallback`, when the viewer can't load
 * the private list (signed out, or hasn't got the replay), show the public summary
 * instead of nothing. `underPlayer` drops the top margin so it sits flush below
 * the video on the viewer page.
 */
export const ReplayQuestions: React.FC<{ productId: string; publicFallback?: boolean; underPlayer?: boolean }> = ({ productId, publicFallback = false, underPlayer = false }) => {
  const { toast } = useToast();
  const sectionRef = useRef<HTMLElement | null>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [isCreator, setIsCreator] = useState(false);
  const [canAnswer, setCanAnswer] = useState(false);
  const [creatorFirst, setCreatorFirst] = useState('the creator');
  const [activeReplay, setActiveReplay] = useState<ReplayQuestionList['activeReplay']>(null);
  const [questions, setQuestions] = useState<ReplayQuestion[]>([]);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [summary, setSummary] = useState<ReplayQuestionSummary | null>(null);

  useEffect(() => {
    let live = true;
    fetchReplayQuestions(productId)
      .then(data => {
        if (!live) return;
        setIsCreator(data.viewerIsCreator);
        setCanAnswer(data.viewerCanAnswer ?? data.viewerIsCreator);
        setCreatorFirst(data.creatorFirst);
        setActiveReplay(data.activeReplay || null);
        // A thread reads top to bottom: oldest first, newest nearest the composer.
        setQuestions([...data.questions].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()));
      })
      .catch(() => {
        if (!live) return;
        setUnavailable(true);
        if (publicFallback) fetchReplayQuestionSummary(productId).then(s => { if (live) setSummary(s); }).catch(() => {});
      })
      .finally(() => { if (live) setLoaded(true); });
    return () => { live = false; };
  }, [productId, publicFallback]);

  // The creator's email links to #questions; the section mounts after the
  // product loads, so the browser's own hash jump has already missed it.
  useEffect(() => {
    if (loaded && window.location.hash === '#questions') {
      (sectionRef.current || document.getElementById('questions'))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loaded, summary]);

  if (unavailable) return summary ? <PublicQuestions data={summary} /> : null;

  const ask = async () => {
    const text = draft.trim();
    if (text.length < QUESTION_MIN || posting) return;
    setPosting(true);
    try {
      const q = await askReplayQuestion(productId, text);
      setQuestions(prev => [...prev, q]);
      setDraft('');
      toast({ title: 'Question sent', description: `We emailed it to ${creatorFirst}. You'll get an email when they answer.` });
    } catch (e: any) {
      toast({ title: 'Question not sent', description: e.message, variant: 'destructive' });
    } finally {
      setPosting(false);
    }
  };

  const hide = async (qid: string) => {
    try {
      await hideReplayQuestion(productId, qid);
      setQuestions(prev => prev.filter(q => q._id !== qid));
    } catch (e: any) {
      toast({ title: 'Could not remove it', description: e.message, variant: 'destructive' });
    }
  };

  const saved = (qid: string) => (answer: string, answeredAt: string) => {
    setQuestions(prev => prev.map(q => (q._id === qid ? { ...q, answer, answeredAt } : q)));
    setEditing(null);
  };

  const tooShort = draft.trim().length < QUESTION_MIN;
  const unanswered = questions.filter(q => !q.answer).length;
  const title = isCreator ? 'Questions from viewers' : `Questions for ${creatorFirst}`;

  return (
    <section
      id="questions"
      ref={sectionRef}
      style={{ marginTop: underPlayer ? 0 : 32, marginBottom: underPlayer ? 22 : 0, scrollMarginTop: 80 }}
      className="flex flex-col gap-4"
    >
      <Heading title={title} sub={[countLine(questions.length, questions.length - unanswered), canAnswer && unanswered > 0 ? `${unanswered} waiting on ${isCreator ? 'your' : 'an'} answer` : ''].filter(Boolean).join(' · ')} />

      {/* Active replay: the host's 24-hour promise, while the window is open. */}
      {activeReplay?.live && (
        <PromiseBox
          until={activeReplay.until}
          text={isCreator
            ? 'You promised an answer within 24 hours to every question asked here.'
            : `Ask anything below and ${creatorFirst} answers within 24 hours.`}
        />
      )}

      {/* The thread: each question a message, the host's answer a reply beneath it. */}
      <div className="flex flex-col gap-4" style={card}>
        {!loaded ? (
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--hj-ink-2)' }} />
        ) : questions.length === 0 ? (
          <p className="m-0" style={{ fontSize: 13.5, color: 'var(--hj-ink-2)' }}>
            {isCreator ? 'No questions yet. When someone asks, we email you and it shows up here.' : `No questions yet. Ask ${creatorFirst} the first one below.`}
          </p>
        ) : (
          questions.map(q => (
            <div key={q._id} className="flex flex-col gap-2.5">
              <div className="flex items-start gap-2.5">
                <Avatar name={q.isMine ? 'You' : q.askerFirst} />
                <div className="flex-1 min-w-0 flex flex-col gap-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--hj-ink)' }}>{q.isMine ? 'You' : q.askerFirst}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--hj-ink-2)' }}>{when(q.createdAt)}</span>
                  </div>
                  <Bubble text={q.question} />
                  {!q.answer && !canAnswer && (
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--hj-ink-2)' }}>Waiting on {creatorFirst}</span>
                  )}
                </div>
              </div>

              {q.answer && editing !== q._id && (
                <div className="flex items-start gap-2.5" style={{ paddingLeft: 24 }}>
                  <Avatar name={creatorFirst} host />
                  <div className="flex-1 min-w-0 flex flex-col gap-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--hj-ink)' }}>{isCreator ? 'You' : creatorFirst}</span>
                      {q.answeredAt && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--hj-ink-2)' }}>{when(q.answeredAt)}</span>}
                    </div>
                    <Bubble text={q.answer} host />
                  </div>
                </div>
              )}

              {canAnswer && (!q.answer || editing === q._id) && (
                <div style={{ paddingLeft: 24 }}>
                  <AnswerBox
                    productId={productId}
                    q={q}
                    onSaved={saved(q._id)}
                    onCancel={editing === q._id ? () => setEditing(null) : undefined}
                  />
                </div>
              )}

              {canAnswer && (
                <div className="flex items-center gap-3 flex-wrap" style={{ paddingLeft: 24 }}>
                  {q.answer && editing !== q._id && (
                    <button type="button" style={ghostBtn} className="inline-flex items-center gap-1" onClick={() => setEditing(q._id)}>
                      <Pencil className="w-3.5 h-3.5" /> Edit answer
                    </button>
                  )}
                  <button type="button" style={ghostBtn} className="inline-flex items-center gap-1" onClick={() => hide(q._id)}>
                    <EyeOff className="w-3.5 h-3.5" /> Remove question
                  </button>
                </div>
              )}
            </div>
          ))
        )}

        {/* The composer, at the foot of the thread like a chat box. */}
        {!isCreator && loaded && (
          <div className="flex items-start gap-2.5" style={{ paddingTop: questions.length ? 12 : 0, borderTop: questions.length ? '1px solid var(--hj-hairline)' : 0 }}>
            <Avatar name="You" />
            <div className="flex-1 min-w-0 flex flex-col gap-2">
              <Textarea
                ref={composerRef}
                placeholder={`Ask ${creatorFirst} about this replay`}
                value={draft}
                onChange={e => setDraft(e.target.value.substring(0, QUESTION_MAX))}
                rows={2}
              />
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <span className="mono-label tabular" style={{ color: 'var(--hj-ink-2)', fontFamily: 'var(--hj-font-mono)' }}>
                  {draft.length}/{QUESTION_MAX}
                </span>
                <button
                  type="button"
                  onClick={ask}
                  disabled={tooShort || posting}
                  className="inline-flex items-center gap-1.5 disabled:cursor-not-allowed"
                  style={pillBtn(tooShort || posting)}
                >
                  {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
                  Ask {creatorFirst}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ReplayQuestions;
